'use client'
import { getProductImages } from 'shared-utils/utils/products'
import { useCallback, useMemo } from 'react'
import { VStack } from '@chakra-ui/react'
import { type ICartItemGroup } from 'ecosystem'
import { blendedUnitPrice, getProductPrice } from 'shared-utils'
import { useVatMode } from '../../../common/hooks/useVatMode'
import type { CartItemDetailProps } from './types'
import { CartItemInfo } from './CartItemInfo'

export const CartItemDetailGroup = ({
  cartItem,
  size = 'sm'
}: Omit<CartItemDetailProps, 'cartItem'> & { cartItem: ICartItemGroup }) => {
  const { items, name, imgUrl } = cartItem.meta.groupType
  const { excludeVat } = useVatMode()

  // Each component's ladder scores against the units actually being bought
  // across the whole bundle line, then charges for one bundle's worth — the
  // same basis `sumCart` uses, so the row and the cart total agree.
  const unitPriceOf = useCallback(
    (item: (typeof items)[number]) =>
      blendedUnitPrice(
        cartItem.qty * (item.originalQty ?? 1),
        getProductPrice(item.product, excludeVat),
        item.product.quantityDiscount
      ),
    [cartItem.qty, excludeVat]
  )

  const totalPrice = useMemo(() => {
    return items.reduce((acc, item) => {
      return acc + unitPriceOf(item) * (item.originalQty ?? 1)
    }, 0)
  }, [items, unitPriceOf])

  return (
    <VStack alignItems="stretch" w="100%">
      <CartItemInfo
        name={name ?? ''}
        qty={cartItem.qty}
        imgUrl={imgUrl}
        price={totalPrice}
        size={size}
      />
      <VStack alignItems="stretch" w="100%" pl={2}>
        {items.map((item) => (
          <CartItemInfo
            key={item.product.id}
            name={item.product.name ?? ''}
            qty={item.qty ?? 1}
            imgUrl={getProductImages(item.product.medias).mainImageUrl}
            price={unitPriceOf(item)}
            size="xs"
          />
        ))}
      </VStack>
    </VStack>
  )
}
